import { useEffect, useId, useState } from 'react';
import type { JSX } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import type { TranslationKeys } from '../../i18n/types.js';

type Translate = (key: keyof TranslationKeys) => string;

/** The three small dialogs of a Studio map's ⋯ menu (P06, P-3): rename, nest, image position. */

export interface RenameMapDialogProps {
  readonly open: boolean;
  readonly name: string;
  readonly t: Translate;
  readonly onSave: (name: string) => void;
  readonly onClose: () => void;
}

/** «Переименовать карту»: one field with the current name. A name the server would refuse (1–50) is not sent. */
export function RenameMapDialog({ open, name, t, onSave, onClose }: RenameMapDialogProps): JSX.Element {
  const [value, setValue] = useState<string>(name);
  const id = useId();
  useEffect(() => {
    if (open) setValue(name);
    // The start values are taken when the dialog opens: a refresh from outside does not erase what is typed.
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const trimmed = value.trim();
  const valid = trimmed.length > 0 && trimmed.length <= 50;
  const submit = (): void => {
    if (!valid) return;
    if (trimmed !== name) onSave(trimmed);
    onClose();
  };
  return (
    <Dialog open={open} onOpenChange={(next): void => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('studio.mapDialog.renameTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('studio.mapDialog.renameTitle')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e): void => {
            e.preventDefault();
            submit();
          }}
        >
          <label htmlFor={id} className="text-meta text-muted-foreground">{t('studio.mapDialog.nameLabel')}</label>
          <Input id={id} autoFocus value={value} maxLength={50} onChange={(e): void => setValue(e.target.value)} />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('studio.mapDialog.cancel')}</Button>
            <Button type="submit" disabled={!valid}>{t('studio.mapDialog.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface NestMapDialogProps {
  readonly open: boolean;
  /** The map's current parent (null — top level). */
  readonly parentId: string | null;
  /** Every map the map may be nested into: not itself, not its own descendants. */
  readonly options: readonly { readonly id: string; readonly name: string }[];
  readonly t: Translate;
  readonly onSave: (parentId: string | null) => void;
  readonly onClose: () => void;
}

/** «Вложить карту»: «Родительская карта» — «— верхний уровень —» and the maps it may go under. */
export function NestMapDialog({ open, parentId, options, t, onSave, onClose }: NestMapDialogProps): JSX.Element {
  const [value, setValue] = useState<string>(parentId ?? '');
  const id = useId();
  useEffect(() => {
    if (open) setValue(parentId ?? '');
    // As above: only the opening takes the start value.
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <Dialog open={open} onOpenChange={(next): void => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('studio.mapDialog.nestTitle')}</DialogTitle>
          <DialogDescription className="sr-only">{t('studio.mapDialog.nestTitle')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e): void => {
            e.preventDefault();
            const next = value === '' ? null : value;
            if (next !== parentId) onSave(next);
            onClose();
          }}
        >
          <label htmlFor={id} className="text-meta text-muted-foreground">{t('studio.mapDialog.parent')}</label>
          <select
            id={id}
            value={value}
            onChange={(e): void => setValue(e.target.value)}
            className="h-9 rounded-md border border-input bg-background px-2 text-ui"
          >
            <option value="">{t('studio.mapDialog.topLevel')}</option>
            {options.map((o) => (
              <option key={o.id} value={o.id}>{o.name}</option>
            ))}
          </select>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>{t('studio.mapDialog.cancel')}</Button>
            <Button type="submit">{t('studio.mapDialog.save')}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export interface ImagePositionDialogProps {
  readonly open: boolean;
  readonly x: number;
  readonly y: number;
  readonly t: Translate;
  readonly onSave: (offset: { readonly x: number; readonly y: number }) => void;
  /** Close and drag the image on the map with the mouse (Esc leaves). */
  readonly onMoveWithMouse: () => void;
  readonly onClose: () => void;
}

const MAX_OFFSET = 100000;

/** «Положение картинки»: the image's shift in pixels, typed — or dragged on the map («Двигать картинку»). */
export function ImagePositionDialog({ open, x, y, t, onSave, onMoveWithMouse, onClose }: ImagePositionDialogProps): JSX.Element {
  const [vx, setVx] = useState<string>(String(x));
  const [vy, setVy] = useState<string>(String(y));
  const idX = useId();
  const idY = useId();
  useEffect(() => {
    if (open) {
      setVx(String(x));
      setVy(String(y));
    }
    // As above: only the opening takes the start values.
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  const nx = Number(vx);
  const ny = Number(vy);
  const valid = vx.trim() !== '' && vy.trim() !== '' && Number.isFinite(nx) && Number.isFinite(ny) && Math.abs(nx) <= MAX_OFFSET && Math.abs(ny) <= MAX_OFFSET;
  return (
    <Dialog open={open} onOpenChange={(next): void => { if (!next) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('studio.mapDialog.positionTitle')}</DialogTitle>
          <DialogDescription>{t('studio.mapDialog.moveImageHint')}</DialogDescription>
        </DialogHeader>
        <form
          className="flex flex-col gap-3"
          onSubmit={(e): void => {
            e.preventDefault();
            if (!valid) return;
            onSave({ x: nx, y: ny });
            onClose();
          }}
        >
          <div className="flex items-end gap-3">
            <div className="flex flex-col gap-1">
              <label htmlFor={idX} className="text-meta text-muted-foreground">X</label>
              <Input id={idX} type="number" step={1} value={vx} onChange={(e): void => setVx(e.target.value)} className="w-28" />
            </div>
            <div className="flex flex-col gap-1">
              <label htmlFor={idY} className="text-meta text-muted-foreground">Y</label>
              <Input id={idY} type="number" step={1} value={vy} onChange={(e): void => setVy(e.target.value)} className="w-28" />
            </div>
            <span className="pb-2 text-meta text-muted-foreground">{t('studio.mapDialog.px')}</span>
          </div>
          <DialogFooter className="sm:justify-between">
            <Button
              type="button"
              variant="outline"
              onClick={(): void => {
                onClose();
                onMoveWithMouse();
              }}
            >
              {t('studio.mapDialog.moveImage')}
            </Button>
            <span className="flex gap-2">
              <Button type="button" variant="outline" onClick={onClose}>{t('studio.mapDialog.cancel')}</Button>
              <Button type="submit" disabled={!valid}>{t('studio.mapDialog.save')}</Button>
            </span>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
